// src/services/apiClient.js
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://platformbackendxi4l0w0c-simpleclass-platform.functions.fnc.fr-par.scw.cloud';

console.log('=== API CLIENT INITIALIZATION ===');
console.log('API_BASE_URL:', API_BASE_URL);
console.log('Environment check:', {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  NODE_ENV: import.meta.env.NODE_ENV,
  MODE: import.meta.env.MODE
});

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Helper functions to get auth and institution data
const getToken = () => {
  const token = localStorage.getItem('authToken');
  console.log('Getting token from localStorage:', token ? '[TOKEN EXISTS]' : '[NO TOKEN]');
  return token;
};

const getCurrentInstitutionId = () => {
  const institutionId = localStorage.getItem('currentInstitutionId');
  console.log('Getting institutionId from localStorage:', institutionId);
  return institutionId;
};

const getCurrentInstitutionSubdomain = () => {
  const institution = localStorage.getItem('selectedInstitution');
  if (institution) {
    try {
      const parsed = JSON.parse(institution);
      console.log('Getting subdomain from selectedInstitution:', parsed.subdomain);
      return parsed.subdomain;
    } catch (e) {
      console.warn('Failed to parse selected institution:', e);
      return null;
    }
  }
  console.log('No selectedInstitution found in localStorage');
  return null;
};

// Request interceptor to add auth token and institution context
apiClient.interceptors.request.use(
  (config) => {
    console.log('=== API REQUEST ===');
    console.log('Method:', config.method?.toUpperCase());
    console.log('URL:', config.url);
    console.log('Base URL:', config.baseURL);
    console.log('Full URL:', `${config.baseURL}${config.url}`);
    
    // Add auth token
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add institution context headers (CRITICAL for authorization!)
    const institutionId = getCurrentInstitutionId();
    const institutionSubdomain = getCurrentInstitutionSubdomain();
    
    if (institutionId) {
      config.headers['X-Institution-Id'] = institutionId;
    }
    
    if (institutionSubdomain) {
      config.headers['X-Institution-Subdomain'] = institutionSubdomain;
    }
    
    console.log('Request headers:', {
      'Content-Type': config.headers['Content-Type'],
      Authorization: token ? 'Bearer [REDACTED]' : 'None',
      'X-Institution-Id': institutionId || 'Not set',
      'X-Institution-Subdomain': institutionSubdomain || 'Not set'
    });
    
    // Add debug logging for development
    if (import.meta.env.NODE_ENV === 'development' || import.meta.env.MODE === 'development') {
      console.log('Request config:', {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        timeout: config.timeout,
        headers: {
          ...config.headers,
          Authorization: token ? 'Bearer [REDACTED]' : config.headers.Authorization
        }
      });
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('=== API RESPONSE SUCCESS ===');
    console.log('Status:', response.status);
    console.log('URL:', response.config.url);
    console.log('Method:', response.config.method?.toUpperCase());
    
    // Log response data (but be careful with sensitive data)
    if (response.config.url?.includes('/live-status') || response.config.url?.includes('/sessions/')) {
      console.log('Response data:', response.data);
    } else {
      console.log('Response data keys:', Object.keys(response.data || {}));
    }
    
    return response.data;
  },
  (error) => {
    console.error('=== API RESPONSE ERROR ===');
    console.error('URL:', error.config?.url);
    console.error('Method:', error.config?.method?.toUpperCase());
    console.error('Status:', error.response?.status);
    console.error('Status Text:', error.response?.statusText);
    console.error('Response Headers:', error.response?.headers);
    console.error('Response Data:', error.response?.data);
    console.error('Request Headers:', error.config?.headers);
    console.error('Error Message:', error.message);
    console.error('Error Code:', error.code);
    
    // Handle different types of errors
    if (error.code === 'ECONNABORTED') {
      console.error('❌ Request timeout');
      error.message = 'Request timeout - server took too long to respond';
    } else if (error.code === 'ERR_NETWORK') {
      console.error('❌ Network error');
      error.message = 'Network error - please check your internet connection';
    } else if (!error.response) {
      console.error('❌ No response received');
      error.message = 'No response from server - please try again';
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.error('❌ Authentication error - redirecting to login');
      // Clear invalid auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('currentInstitutionId');
      localStorage.removeItem('selectedInstitution');
      
      // Redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Handle institution context errors
    if (error.response?.status === 403) {
      console.error('❌ Authorization error - may be institution context issue');
    }
    
    // Handle not found errors
    if (error.response?.status === 404) {
      console.error('❌ Resource not found');
    }
    
    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('❌ Server error');
      error.message = 'Server error - please try again later';
    }
    
    return Promise.reject(error);
  }
);

// Debug function to check authentication state
apiClient.debugAuthState = () => {
  console.log('=== AUTH STATE DEBUG ===');
  console.log('Token:', getToken() ? '[EXISTS]' : '[MISSING]');
  console.log('Institution ID:', getCurrentInstitutionId());
  console.log('Institution Subdomain:', getCurrentInstitutionSubdomain());
  console.log('LocalStorage keys:', Object.keys(localStorage));
};

export default apiClient;